import type { QuestionBody } from '../domain/entities/content.entity';

/**
 * Regras do gabarito num lugar só — a célula que **mostra** a resposta, o
 * seletor que a **altera** e o card da questão.
 *
 * Este arquivo já foi maior: tinha `validateAnswerLetter`, `answerLetters`,
 * `alternativeIdForLetter` e `normalizeAnswerInput`, todos existindo para
 * conferir uma letra **digitada** — recusar `D` numa questão de A–C, recusar
 * `AB`, recusar `2`, normalizar caixa e espaço. Com o gabarito editado por
 * `<select>` montado a partir das alternativas da própria questão, nenhuma
 * dessas entradas é expressável: a validação virou a lista de opções.
 *
 * O que sobrou é leitura. Quem valida do lado que decide continua sendo o
 * `SetQuestionAnswerKeyUseCase` no back — e ele não mudou.
 */

/**
 * Valor da opção "Anulada" no seletor. Não colide com `id` de alternativa
 * (`a`, `b`, … ou `alt27`) porque nenhum id tem `:`.
 */
export const ANNULLED_OPTION = 'answer:annulled';

/**
 * Anulada? Só dissertativa não pode ser, e o campo ausente no documento
 * significa "não" (é assim que o back grava: só quando `true`). Existe como
 * função porque quatro telas fazem a pergunta, e a união do `QuestionBody`
 * obrigaria cada uma a repetir a checagem de formato.
 */
export function isAnnulled(body: QuestionBody | null | undefined): boolean {
  return body?.format === 'MULTIPLE_CHOICE' && body.annulled === true;
}

/**
 * Letra da alternativa correta, em maiúscula. `null` quando não existe uma:
 * dissertativa, questão anulada, ou `correctAlternativeId` que não casa com
 * nenhuma alternativa (corpo inconsistente, que a tela mostra como `—` em vez
 * de esconder a questão).
 */
export function correctAlternativeLetter(body: QuestionBody): string | null {
  if (body.format !== 'MULTIPLE_CHOICE') return null;
  if (isAnnulled(body)) return null;
  const correct = body.alternatives.find(
    (alt) => alt.id === body.correctAlternativeId,
  );
  return correct ? correct.id.toUpperCase() : null;
}

/**
 * As opções do seletor: uma por alternativa **desta** questão, na ordem, mais
 * "Anulada" no fim.
 *
 * O `value` é o `id` da alternativa, não a letra — é o `id` que o back guarda,
 * então não há conversão letra↔id no caminho (era mais uma função só para
 * desfazer a tradução que a UI tinha feito). O rótulo é o `id` em maiúscula,
 * o mesmo que a célula mostra quando não está em edição.
 */
export function answerOptions(
  body: QuestionBody,
): { value: string; label: string }[] {
  if (body.format !== 'MULTIPLE_CHOICE') return [];
  return [
    ...body.alternatives.map((alt) => ({
      value: alt.id,
      label: alt.id.toUpperCase(),
    })),
    { value: ANNULLED_OPTION, label: 'Anulada' },
  ];
}

/** A opção que descreve o estado atual da questão. */
export function currentAnswerOption(body: QuestionBody): string {
  if (body.format !== 'MULTIPLE_CHOICE') return '';
  return isAnnulled(body) ? ANNULLED_OPTION : (body.correctAlternativeId ?? '');
}
